import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage, Language } from "@/hooks/useLanguage";
import { linkLayout } from "@/lib/site/hosts";
import { alternatePublicPath, languageFromPath } from "@/lib/site/publicPages";

/**
 * Syncs URL language prefix with language state
 * Updates URL when language changes and updates language when URL changes
 * Redirects paths without language prefix to include it
 */
export function LanguageSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const isUpdatingUrlRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const previousLanguageRef = useRef(language);

  // When the path includes /cs|en/, treat the URL as the source of truth for language.
  // IMPORTANT: depend only on location.pathname — do NOT depend on `language`. Otherwise when the
  // user picks a new language in the UI, this runs before navigate() updates the path and would
  // reset language back from the stale URL segment (breaking legal pages and the header switcher).
  useEffect(() => {
    // `/cs/…`, `/en/…` and the Czech pages at the root of zettl.cz (`/kontakt`).
    const urlLang = languageFromPath(location.pathname);

    if (urlLang) {
      setLanguage((prev) => (prev === urlLang ? prev : urlLang));
      hasInitializedRef.current = true;
    }
  }, [location.pathname, setLanguage]);

  useEffect(() => {
    const path = location.pathname;
    if (languageFromPath(path) || path === "/" || !hasInitializedRef.current) return;

    isUpdatingUrlRef.current = true;
    const newPath = `/${language}${path}`;
    navigate(newPath, { replace: true });
    setTimeout(() => {
      isUpdatingUrlRef.current = false;
    }, 100);
  }, [location.pathname, language, navigate]);

  // Sync language state to URL when language changes (but only if URL already has a language).
  // Only when `language` itself changed since the last run: if just the path changed (a
  // <Link to="/en">), the first effect is still moving state to the URL, and acting on the
  // stale language here would bounce the visitor back to the old prefix.
  useEffect(() => {
    const languageChanged = previousLanguageRef.current !== language;
    previousLanguageRef.current = language;
    if (!languageChanged || !hasInitializedRef.current) return;

    const path = location.pathname;

    // A public page: the same page in the other language (`/kontakt` ↔ `/en/contact`).
    const alternate = alternatePublicPath(path, language, linkLayout(window.location.hostname));
    if (alternate) {
      if (alternate !== path && !isUpdatingUrlRef.current) {
        isUpdatingUrlRef.current = true;
        navigate(`${alternate}${location.search}${location.hash}`, { replace: true });
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 100);
      }
      return;
    }

    const langMatch = path.match(/^\/(cs|en)(\/|$)/);

    if (langMatch) {
      const urlLang = langMatch[1] as Language;
      if (urlLang !== language && !isUpdatingUrlRef.current) {
        isUpdatingUrlRef.current = true;
        // Replace language prefix in URL
        const newPath = path.replace(/^\/(cs|en)/, `/${language}`);
        navigate(newPath, { replace: true });
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 100);
      }
    }
  }, [language, navigate, location.pathname, location.search, location.hash]);

  return null;
}
